import { useContext, useEffect, useState } from "react";

import ParamsContext from "context/params/paramsContext";
import { Filter, FILTER_NAMES, sortAndReturnArray } from "shared";

import { Company, SECTION_TITLES } from "../types";

export const useListCompanies = (searchValue: string) => {
    const {
        companyFilteredValues,
        filterDropdownOptions,
        setQueryParams,
        toggleTableRowDeselection,
        resetFilterParams
    } = useContext(ParamsContext);

    const [allCompanies, setAllCompanies] = useState<Company[]>([]);

    useEffect(() => {
        const getSearchResults = (companies: Filter[]) =>
            companies.filter(company =>
                company.name.toLowerCase().includes(searchValue.toLowerCase())
            );

        const getAllCompanies = () => {
            const companies =
                filterDropdownOptions[FILTER_NAMES.CompanyId] || [];

            const companiesAfterSearch = getSearchResults(companies);

            const filteredValuesAfterSearch = getSearchResults(
                companyFilteredValues
            );

            const notSelectedOptions = companiesAfterSearch.filter(
                company =>
                    companyFilteredValues.findIndex(
                        selectedCompany => selectedCompany.id === company.id
                    ) === -1
            );

            const filteredLength = filteredValuesAfterSearch.length;
            const notFilteredLength = notSelectedOptions.length;

            return [
                ...(!filteredLength && notFilteredLength
                    ? [
                          {
                              id: "null",
                              name: SECTION_TITLES.AllCompanies,
                              count: notFilteredLength
                          } as Company
                      ]
                    : []),
                ...(filteredLength
                    ? [
                          {
                              id: "null",
                              name: SECTION_TITLES.SelectedCompanies,
                              count: filteredLength
                          } as Company
                      ]
                    : []),
                ...sortAndReturnArray(filteredValuesAfterSearch, "name"),
                ...(filteredLength && notFilteredLength
                    ? [
                          {
                              id: "null",
                              name: SECTION_TITLES.OtherCompanies,
                              count: notFilteredLength
                          } as Company
                      ]
                    : []),
                ...sortAndReturnArray(notSelectedOptions, "name")
            ];
        };

        setAllCompanies(getAllCompanies());
    }, [searchValue, companyFilteredValues, filterDropdownOptions]);

    const updateSelectedCompaniesArray = (newCompany: Filter) => {
        const currentSelectedCompanies = [...companyFilteredValues];

        const index = currentSelectedCompanies.findIndex(
            company => company.id === newCompany.id
        );

        if (index === -1) {
            return [...currentSelectedCompanies, newCompany];
        } else {
            currentSelectedCompanies.splice(index, 1);
            return currentSelectedCompanies;
        }
    };

    const sendToQueryParams = (newCompany: Filter) => {
        const updatedSelectedCompanies =
            updateSelectedCompaniesArray(newCompany);

        const paramsPayload = {
            [FILTER_NAMES.CompanyId]: updatedSelectedCompanies.map(
                singleValue => String(singleValue.id)
            )
        };

        const filteredValuesPayload = {
            filterName: FILTER_NAMES.CompanyId,
            value: updatedSelectedCompanies
        };

        setQueryParams({
            params: paramsPayload,
            resetPage: true,
            filteredValues: filteredValuesPayload
        });

        toggleTableRowDeselection();
    };

    const companiesCount = allCompanies.length;

    return {
        allCompanies,
        companyFilteredValues,
        companiesCount,
        sendToQueryParams,
        resetFilterParams
    };
};
